import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwipe } from '../context/SwipeContext';
import { MessageCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getUserPhoto } from '../utils/photoUtils';
import { formatMessageTime, truncateText } from '../utils/timeUtils';

const MatchesPage = () => {
  const navigate = useNavigate();
  const { matches, loadMatches } = useSwipe();
  const [selectedMatch, setSelectedMatch] = useState(null);
  const matchesContainerRef = useRef(null);
  
  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);

  // Reset pull-to-refresh state and scroll position
  useEffect(() => {
    // Reset scroll to top when component mounts
    const scrollableParent = matchesContainerRef.current?.parentElement;
    if (scrollableParent) {
      scrollableParent.scrollTop = 0;
    }
    
    // Reset pull state
    setPullDistance(0);
    setIsRefreshing(false);
    isPulling.current = false;
    
    // Attach touch handlers to the scrollable parent
    if (scrollableParent) {
      scrollableParent.addEventListener('touchstart', handleTouchStart, { passive: false });
      scrollableParent.addEventListener('touchmove', handleTouchMove, { passive: false });
      scrollableParent.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      // Cleanup
      setPullDistance(0);
      setIsRefreshing(false);
      isPulling.current = false;
      
      // Remove event listeners
      if (scrollableParent) {
        scrollableParent.removeEventListener('touchstart', handleTouchStart);
        scrollableParent.removeEventListener('touchmove', handleTouchMove);
        scrollableParent.removeEventListener('touchend', handleTouchEnd);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - handlers won't change, refs are stable

  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedMatch) {
        setSelectedMatch(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedMatch]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedMatch) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedMatch]);

  const handlePhotoClick = (match, e) => {
    e.stopPropagation(); // Prevent navigation to messages
    setSelectedMatch(match);
  };

  // Pull-to-refresh handlers
  const handleTouchStart = useCallback((e) => {
    // Get the actual scrollable container from App.js wrapper
    const scrollableParent = matchesContainerRef.current?.parentElement;
    if (!scrollableParent) return;
    
    // Only enable pull-to-refresh when scrolled to top
    if (scrollableParent.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling.current) return;
    
    const touchY = e.touches[0].clientY;
    const distance = touchY - touchStartY.current;
    
    // Only allow downward pull
    if (distance > 0) {
      // Apply resistance
      const maxPull = 100;
      const resistance = Math.min(distance * 0.5, maxPull);
      setPullDistance(resistance);
      
      // Prevent default scrolling when pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    
    isPulling.current = false;
    
    // Trigger refresh if pulled down enough
    if (pullDistance > 50 && !isRefreshing) {
      setIsRefreshing(true);
      
      try {
        await loadMatches();
        
        // Show success state briefly
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 500);
      } catch (error) {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      // Snap back if not pulled enough
      setPullDistance(0);
    }
  }, [pullDistance, isRefreshing, loadMatches]);

  if (matches.length === 0) {
    return (
      <div className="flex items-center justify-center p-4 min-h-full bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <MessageCircle className="w-12 h-12 text-purple-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No matches yet</h2>
          <p className="text-gray-600 mb-6">Keep swyping to find your perfect match!</p>
          <Button
            onClick={() => navigate('/swipe')}
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
          >
            Start Swiping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-full">
      {/* Matches List */}
      <div 
        ref={matchesContainerRef}
        className="max-w-4xl mx-auto"
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: isPulling.current ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {/* Pull-to-refresh indicator */}
        {pullDistance > 0 && (
          <div 
            className="flex justify-center py-4"
            style={{
              transform: `translateY(${Math.min(pullDistance - 40, 20)}px)`,
              opacity: Math.min(pullDistance / 50, 1)
            }}
          >
            <div className="bg-white rounded-full shadow-lg px-4 py-2 flex items-center gap-2">
              {isRefreshing ? (
                <>
                  <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-purple-600 font-medium">Refreshing...</span>
                </>
              ) : (
                <>
                  <span className="text-lg">↓</span>
                  <span className="text-sm text-gray-600">
                    {pullDistance > 50 ? 'Release to refresh' : 'Pull to refresh'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        
        {matches.map((match, index) => {
          const lastMessage = match.last_message;
          const hasUnread = match.unread_count > 0;
          const isOnline = match.is_online;
          const isNewMatch = !lastMessage; // New match = no messages exchanged yet
          
          return (
            <div
              key={match.id}
              className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                index !== matches.length - 1 ? 'border-b border-gray-100' : ''
              }`}
              onClick={() => navigate(`/messages/${match.id}`)}
            >
              <div className="flex items-center gap-3">
                {/* Profile Photo with Online Indicator */}
                <div 
                  className="relative flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={(e) => handlePhotoClick(match, e)}
                >
                  <img
                    src={getUserPhoto(match)}
                    alt={match.name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  {/* Online Status Indicator */}
                  {isOnline && (
                    <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                  )}
                  {/* New Match Indicator - Purple Heart */}
                  {isNewMatch && (
                    <div className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-gradient-to-br from-pink-500 to-purple-600 border-2 border-white rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Message Info */}
                <div className="flex-1 min-w-0">
                  {/* Name and Distance */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold text-gray-900 truncate ${hasUnread ? 'font-bold' : ''}`}>
                        {match.name}
                      </h3>
                      {/* Distance */}
                      {match.distance_km !== null && match.distance_km !== undefined && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <span>📍</span>
                          <span>{Math.round(match.distance_km)} km away</span>
                        </p>
                      )}
                    </div>
                    {/* Timestamp */}
                    <span className={`text-xs flex-shrink-0 ml-2 ${hasUnread ? 'text-purple-600 font-semibold' : 'text-gray-500'}`}>
                      {lastMessage?.timestamp ? formatMessageTime(lastMessage.timestamp) : ''}
                    </span>
                  </div>

                  {/* Last Message Preview */}
                  <div className="flex items-center justify-between">
                    <p className={`text-sm truncate ${hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                      {lastMessage ? (
                        <>
                          {lastMessage.from_me && <span className="text-gray-400">You: </span>}
                          {truncateText(lastMessage.text, 45)}
                        </>
                      ) : (
                        <span className="text-gray-400 italic">Start a conversation...</span>
                      )}
                    </p>
                    
                    {/* Unread Badge */}
                    {hasUnread && (
                      <div className="flex-shrink-0 ml-2 min-w-[20px] h-5 px-1.5 bg-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-xs text-white font-semibold">
                          {match.unread_count > 99 ? '99+' : match.unread_count}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Photo Lightbox Modal */}
      {selectedMatch && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setSelectedMatch(null)}
        >
          {/* Modal Content */}
          <div 
            className="relative max-w-4xl w-full max-h-[90vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedMatch(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:bg-white/10 rounded-full transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Photo */}
            <div className="relative bg-gray-900 rounded-lg overflow-hidden">
              <img
                src={getUserPhoto(selectedMatch)}
                alt={selectedMatch.name}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
              
              {/* Photo Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6">
                <div className="text-white">
                  {/* Name, Age, and Online Status */}
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl font-bold">
                      {selectedMatch.name}, {selectedMatch.age}
                    </h2>
                    {selectedMatch.is_online && (
                      <div className="flex items-center gap-1 bg-green-500/20 px-2 py-1 rounded-full">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-green-400 font-medium">Online</span>
                      </div>
                    )}
                  </div>

                  {/* Bio - Full version */}
                  {selectedMatch.bio && (
                    <p className="text-sm text-white/90 italic">
                      "{selectedMatch.bio}"
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Helper Text */}
            <p className="text-center text-white/60 text-sm mt-4">
              Click outside or press ESC to close
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchesPage;
